import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import useEditionDndActions from '../customHooks/useEditionDndActions';
import DndCard from '../DndCard';
import useEditionButtonActions from '../customHooks/useEditionButtonActions';
import { reorderEditionCards } from '@/store/slices/components/dashboard/DashboardSlice';
import DragOverlayCard from './DragOverlayCard';
import getCardRenderConfig from './utils';

export default function DndList({ tabId, sectionId, cards, type = 'card' }) {
  const dispatch = useDispatch();
  const { toggleHideCard, toggleExpand } = useEditionButtonActions(tabId);
  const { edition } = useSelector((state) => state.DashboardSlice);

  const handleReorder = useCallback(
    (nextItems) => {
      if (!tabId || !sectionId) return;

      dispatch(
        reorderEditionCards({
          tabId,
          sectionId,
          orderedIds: nextItems.map((item) => item.id),
        })
      );
    },
    [dispatch, sectionId, tabId]
  );
  const { items, activeItem, onDragStart, onDragEnd, onDragCancel } =
    useEditionDndActions(cards, { onReorder: handleReorder });

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragStart={edition.active ? onDragStart : undefined}
      onDragEnd={edition.active ? onDragEnd : undefined}
      onDragCancel={edition.active ? onDragCancel : undefined}
    >
      <SortableContext
        items={items.map((item) => item.id)}
        strategy={horizontalListSortingStrategy}
      >
        {items.map((item) => {
          const { Component, props } = getCardRenderConfig({
            tabId,
            sectionId,
            item,
          });
          if (!Component) return null;

          return (
            <DndCard
              key={item.id}
              id={item.id}
              type={type}
              withExpand={type === 'card' && !item.alwaysExpanded}
              visible={item.visible}
              expanded={item.expanded}
              toggleHide={() => toggleHideCard(sectionId, item.id)}
              toggleExpand={() => toggleExpand(sectionId, item.id)}
            >
              <Component {...props} />
            </DndCard>
          );
        })}
      </SortableContext>
      <DragOverlay>
        <DragOverlayCard
          item={activeItem}
          tabId={tabId}
          sectionId={sectionId}
        />
      </DragOverlay>
    </DndContext>
  );
}
